// ─────────────────────────────────────────────────────────────────────────────
// navOptions.ts — Shared option lists for navigation / customization UI
//
// Used by both the desktop dropdowns (Navbar.tsx, ThemeSelector.tsx) and the
// mobile bottom-sheet equivalents (MobileTabBar + mobileSheetContent/*).
// Single source of truth — neither surface should redefine these inline.
// ─────────────────────────────────────────────────────────────────────────────

import type {
  FrontEndPattern, FrontEndFramework, BackEndArchitecture, CVLayout,
} from '@/types/cv.types';

export const FE_PATTERNS: { value: FrontEndPattern; label: string; desc: string }[] = [
  { value: 'flux',   label: 'Flux / Redux',    desc: 'Unidirectional data flow via Redux store' },
  { value: 'mvc',    label: 'MVC',             desc: 'Model → View → Controller handler layer' },
  { value: 'mvvm',   label: 'MVVM',            desc: 'ViewModel hooks, purely declarative View' },
  { value: 'atomic', label: 'Atomic Design',   desc: 'Atoms → Molecules → Organisms hierarchy' },
];

export const FE_FRAMEWORKS: { value: FrontEndFramework; label: string; devPort: number; prodHost: string }[] = [
  { value: 'react-redux', label: 'React + Redux', devPort: 3000, prodHost: 'johncronogue.com' },
  { value: 'angular',     label: 'Angular 17',    devPort: 4200, prodHost: 'angular.johncronogue.com' },
  { value: 'vue',         label: 'Vue 3 + Pinia', devPort: 3001, prodHost: 'vue.johncronogue.com' },
  { value: 'svelte',      label: 'Svelte 5',      devPort: 3002, prodHost: 'svelte.johncronogue.com' },
  { value: 'solidjs',     label: 'SolidJS',       devPort: 3003, prodHost: 'solid.johncronogue.com' },
  { value: 'blazor',      label: 'Blazor WASM',   devPort: 5251, prodHost: 'blazor.johncronogue.com' },
];

// Dev: target the framework's local dev server port.
// Prod: target its real subdomain — never link a visitor's own localhost.
export function frameworkHref(f: { devPort: number; prodHost: string }) {
  return import.meta.env.DEV ? `http://localhost:${f.devPort}` : `https://${f.prodHost}`;
}

// Dev: show the local port, useful for jumping between running dev servers.
// Prod: a port number means nothing to a visitor, so show nothing instead —
// only the active framework still gets a badge ("Active").
export function frameworkBadge(f: { devPort: number }, isCurrent: boolean): string | null {
  if (isCurrent) return 'Active';
  return import.meta.env.DEV ? `:${f.devPort}` : null;
}

export const BACKENDS: { value: BackEndArchitecture; label: string; lang: string; desc: string }[] = [
  { value: 'nlayer',             label: 'N-Layer',          lang: '.NET 10', desc: 'UI → BLL → DAL' },
  { value: 'clean-architecture', label: 'Clean Arch + DDD', lang: '.NET 10', desc: 'API / App / Domain / Infra' },
  { value: 'nodejs',             label: 'Node.js',          lang: 'TS',      desc: 'Express REST API' },
  { value: 'hexagonal',          label: 'Hexagonal',        lang: '.NET 10', desc: 'Ports & Adapters' },
  { value: 'vertical-slice',     label: 'Vertical Slice',   lang: '.NET 10', desc: 'Feature-first slices' },
];

// defaultScheme: when set, auto-applies that colour scheme when the layout is selected
export interface LayoutOption {
  value:          CVLayout;
  label:          string;
  desc:           string;
  icon:           string;
  defaultScheme?: string;
}

export const LAYOUTS: LayoutOption[] = [
  { value: 'classic',   label: 'Classic',     desc: 'Dashboard · sidebar · animated widgets',             icon: '◈' },
  { value: 'executive', label: 'Executive',   desc: 'Section stack · clean · professional',                icon: '≡' },
  { value: 'scholarly', label: 'Scholarly',   desc: 'Elite collegiate · education-first · gold',          icon: 'Φ', defaultScheme: 'collegiate-gold' },
  { value: 'warzone',   label: 'Warzone',     desc: 'Gritty · projects-first · Mad Max',                   icon: '⚡', defaultScheme: 'warzone-rust' },
  { value: 'superhero', label: 'Superhero',   desc: 'Marvel / DC · comic hero · action dossier',          icon: '⬡', defaultScheme: 'superhero-cosmic' },
  { value: 'scifi',     label: 'Sci-Fi',      desc: 'Blueprint UI · stark hologram · cold cyan',          icon: '◉', defaultScheme: 'stark-scifi' },
  { value: 'fantasy',   label: 'Fantasy',     desc: 'Cosy Tolkien · parchment · warm earth',               icon: '✦', defaultScheme: 'cosy-fantasy' },
  { value: 'empire',    label: 'Empire',      desc: 'Imperial Rome · marble · laurels · medals',          icon: 'Ψ', defaultScheme: 'empire-imperial' },
  { value: 'cartoon',   label: 'Cartoon',     desc: 'Comic panels · bold outlines · pop colour',          icon: '◐', defaultScheme: 'cartoon-pop' },
  { value: 'pixel',     label: '8-bit Pixel', desc: 'NES retro · quest log · skill tree',                  icon: '▦', defaultScheme: 'pixel-retro' },
];
